import { t } from '@lingui/macro'
import styled from 'styled-components'
import { useEventListener } from 'usehooks-ts'

import { Modal } from '@/@bothrs-ui/web/components/modal/Modal'

import type { ComponentPropsWithoutRef, MouseEvent } from 'react'

/* ------------------------------------------------------------------------------------------------- *
 * ImagePreviewModal
 * ------------------------------------------------------------------------------------------------- */

type ImagePreviewModalRootProps = ComponentPropsWithoutRef<'div'> & {
  isVisible: boolean
  onClose: () => void
}

/**
 * Full screen dialog on a dark backdrop; tapping around the image or pressing
 * Escape closes it.
 *
 * Full screen, the stage covers Modal's own backdrop, so the stage handles the tap.
 */
const ImagePreviewModalRoot = ({ isVisible, onClose, children, ...props }: ImagePreviewModalRootProps) => {
  // Modal has no keyboard handling of its own.
  useEventListener('keydown', (event) => {
    if (isVisible && event.key === 'Escape') {
      onClose()
    }
  })

  return (
    <Modal isVisible={isVisible} onClose={onClose} fullScreen backdropColor='rgba(0, 0, 0, 0.85)'>
      <StStage
        role='dialog'
        aria-modal='true'
        aria-label={t({
          id: 'image-preview.dialog.label',
          comment: 'Accessible name of the full screen image preview',
          message: 'Afbeelding op volledig scherm',
        })}
        onClick={onClose}
        {...props}
      >
        {children}
      </StStage>
    </Modal>
  )
}

/* ------------------------------------------------------------------------------------------------- *
 * ImagePreviewModal.Image
 * ------------------------------------------------------------------------------------------------- */

/** `alt` is required: describe the image, or pass `''` only when it is purely decorative. */
type ImagePreviewModalImageProps = ComponentPropsWithoutRef<'img'> & { alt: string }

/** A tap on the image itself does not close the modal. Once loaded, it covers the Spinner. */
const ImagePreviewModalImage = ({ onClick, ...props }: ImagePreviewModalImageProps) => (
  <StImage
    onClick={(event: MouseEvent<HTMLImageElement>) => {
      event.stopPropagation()
      onClick?.(event)
    }}
    {...props}
  />
)

/* ------------------------------------------------------------------------------------------------- *
 * ImagePreviewModal.CloseButton
 * ------------------------------------------------------------------------------------------------- */

type ImagePreviewModalCloseButtonProps = ComponentPropsWithoutRef<'button'>

/** Takes focus on open, so a keyboard user lands in the preview rather than behind it. */
const ImagePreviewModalCloseButton = (props: ImagePreviewModalCloseButtonProps) => (
  <StCloseButton
    type='button'
    // The preview is a dialog, so focus has to move into it when it opens.
    autoFocus
    aria-label={t({
      id: 'image-preview.close',
      comment: 'Label of the button that closes the full screen image',
      message: 'Sluiten',
    })}
    {...props}
  />
)

/* ------------------------------------------------------------------------------------------------- *
 * ImagePreviewModal.Spinner
 * ------------------------------------------------------------------------------------------------- */

type ImagePreviewModalSpinnerProps = ComponentPropsWithoutRef<'div'>

/** Sits behind the Image, so it shows only until the image has loaded and covers it. */
const ImagePreviewModalSpinner = (props: ImagePreviewModalSpinnerProps) => <StSpinner {...props} />

/* ------------------------------------------------------------------------------------------------- *
 * Compound export
 * ------------------------------------------------------------------------------------------------- */

const ImagePreviewModal = Object.assign(ImagePreviewModalRoot, {
  Image: ImagePreviewModalImage,
  CloseButton: ImagePreviewModalCloseButton,
  Spinner: ImagePreviewModalSpinner,
})

export { ImagePreviewModal }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

const StStage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;
`

/* Stacking: Spinner (auto) < Image (1) < CloseButton (2). The loaded image hides the spinner. */
const StImage = styled.img`
  position: relative;
  z-index: 1;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
`

const StCloseButton = styled.button`
  position: absolute;
  z-index: 2;
  top: ${({ theme }) => theme.UI.SpacingPx.Space5};
  right: ${({ theme }) => theme.UI.SpacingPx.Space5};
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: ${({ theme }) => theme.Colors.Background.White};
  width: ${({ theme }) => theme.UI.SpacingPx.Space10};
  height: ${({ theme }) => theme.UI.SpacingPx.Space10};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: ${({ theme }) => theme.UI.SpacingPx.Space5};
    height: 2px;
    background-color: ${({ theme }) => theme.Colors.Background.White};
    transform: rotate(45deg);
  }

  &::after {
    transform: rotate(-45deg);
  }
`

const StSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${({ theme }) => theme.UI.SpacingPx.Space10};
  height: ${({ theme }) => theme.UI.SpacingPx.Space10};
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: ${({ theme }) => theme.Colors.Background.White};
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }
`
